import React, { useState, useRef, useEffect } from 'react';
import { Button, Modal, Alert } from 'react-bootstrap';
import { FaCamera, FaUpload } from 'react-icons/fa';

const IMAGE_UPLOAD_URL = "https://mixta39.dev/phpapi/api/upload_image.php";

const ImageUploader = ({ onImageUploaded, id }) => {
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [message, setMessage] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);  // Nuevo ref para el input de archivo
  const [stream, setStream] = useState(null);

  // Asignar el stream al video cuando se muestra el modal
  useEffect(() => {
    if (showCameraModal && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.onloadedmetadata = () => {
        videoRef.current.play();
      };
    }
  }, [showCameraModal, stream]);

  // Limpiar el stream al desmontar o al cerrar el modal
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  const startCamera = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      let selectedDeviceId = null;

      if (videoDevices.length > 0) {
        const backCamera = videoDevices.find(device => {
          const label = device.label.toLowerCase();
          return label.includes('back') || label.includes('environment') || label.includes('trasera');
        });
        selectedDeviceId = backCamera ? backCamera.deviceId : videoDevices[0].deviceId;
      }

      const constraints = selectedDeviceId
        ? { video: { deviceId: { exact: selectedDeviceId } }, audio: false }
        : { video: true, audio: false };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);
      setShowCameraModal(true);
    } catch (err) {
      console.error("Error al acceder a la cámara:", err);
      setMessage({ text: "Error al acceder a la cámara. Verifica permisos y entorno HTTPS/localhost.", type: "danger" });
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(async (blob) => {
        const file = new File([blob], "photo.png", { type: "image/png" });
        await uploadImage(file);
        closeCamera();
      }, 'image/png');
    }
  };

  const closeCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCameraModal(false);
  };

  const uploadImage = async (file) => {
    if (file) {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('id', id);

      try {
        setMessage({ text: "Subiendo imagen...", type: "info" });
        const response = await fetch(IMAGE_UPLOAD_URL, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          throw new Error("Error al subir la imagen.");
        }

        const data = await response.json();
        if (data.success) {
          onImageUploaded(data.imageUrl);
          setMessage({ text: "La imagen se ha subido correctamente.", type: "success" });
          setTimeout(() => setMessage(null), 3000);
        } else {
          setMessage({ text: "Hubo un error al subir la imagen.", type: "danger" });
        }
      } catch (err) {
        console.error(err);
        setMessage({ text: "Hubo un error al subir la imagen.", type: "danger" });
      }
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      await uploadImage(file);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-center">
        <Button variant="primary" className="me-2" onClick={startCamera}>
          <FaCamera /> Capturar Imagen
        </Button>
        <Button variant="light" className="me-2" onClick={() => fileInputRef.current && fileInputRef.current.click()}>
          <FaUpload /> Subir Imagen
        </Button>
        {/* Usamos la ref en lugar de document.getElementById */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </div>
      {message && <Alert variant={message.type} className="mt-2">{message.text}</Alert>}
      <Modal show={showCameraModal} onHide={closeCamera} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Capturar Imagen</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <div className="camera-container">
            <video 
              ref={videoRef} 
              autoPlay 
              playsInline 
              muted
              style={{ width: '100%', maxHeight: '480px', objectFit: 'contain' }} 
              id="videoplayer"
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeCamera}>Cancelar</Button>
          <Button variant="primary" onClick={capturePhoto}>
            <FaCamera /> Capturar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ImageUploader;
